"use client"
import { useQualityChanges } from "@/hooks/use-quality-presentations"


import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef } from "react"
import { toast } from "sonner"

import type { MissedReplyCursor } from "@/lib/conversation-quality/analytics/missed-replies"

import type { ChangesCursor } from "@/lib/conversation-quality/suggestions/changes"
import type { FindNowReport } from "@/lib/conversation-quality/suggestions/schema"
import type {
  ConversationOutcomesResult,
  ConversationVolumeResult,
  MissedReplyConversationsInput,
  QualityAgentOption,
  QualityPeriodInput,
  QualitySuggestionsInput,
  QualitySuggestionsResult,
  SelfLearningSummaryResult,
  ServiceMetricsResult,
  UnresolvedTopicsResult,
} from "@/server-actions/conversation-quality"
import type { QualityAccessResult } from "@/server-actions/conversation-quality-access"
import {
  getQualityDigestSettingsAction,
  type QualityDigestSettingsView,
} from "@/server-actions/conversation-quality-digest"
import {
  listAgentQualityChangesAction,
  type QualityChangeView,
} from "@/server-actions/conversation-quality-changes"
import { getSuggestionInstructionsAction } from "@/server-actions/conversation-quality-suggestions"

import { readQuality } from "./quality-reads"

export const conversationQualityQueryKey = ["conversation-quality"] as const

export const qualitySuggestionsQueryKey = [...conversationQualityQueryKey, "suggestions"] as const

export const findImprovementsQueryKey = [...conversationQualityQueryKey, "find-now"] as const

const FIND_NOW_POLL_MS = 5_000

/** Whether the feature is on for the workspace; the sections it produces load only while it is. */
export function useQualityAccessQuery() {
  return useQuery<QualityAccessResult>({
    queryKey: [...conversationQualityQueryKey, "access"],
    queryFn: async () => {
      const result = await readQuality("access", {})
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    staleTime: 60_000,
  })
}

export function useConversationVolumeQuery(input: QualityPeriodInput, enabled = true) {
  return useQuery<ConversationVolumeResult>({
    queryKey: [...conversationQualityQueryKey, "volume", input],
    queryFn: async () => {
      const result = await readQuality("volume", input)
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    enabled,
    // The numbers move only as sessions close, so a minute of staleness costs nothing and
    // spares the aggregate queries a re-run on every remount.
    staleTime: 60_000,
  })
}

/** One Missed replies cause's conversations, a page at a time: behind an open Volume row, and Overview's waiting card. */
export function useMissedReplyConversationsQuery(input: Omit<MissedReplyConversationsInput, "cursor">) {
  return useInfiniteQuery({
    queryKey: [...conversationQualityQueryKey, "missed-replies", "conversations", input],
    initialPageParam: null as MissedReplyCursor | null,
    queryFn: async ({ pageParam }) => {
      const result = await readQuality("missedReplyConversations", { ...input, cursor: pageParam })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 60_000,
  })
}

export function useMissedReplyWordingsQuery(input: QualityPeriodInput) {
  return useQuery({
    queryKey: [...conversationQualityQueryKey, "missed-replies", "wordings", input],
    queryFn: async () => {
      const result = await readQuality("missedReplyWordings", input)
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    staleTime: 60_000,
  })
}

export function useConversationServiceMetricsQuery(input: QualityPeriodInput, enabled = true) {
  return useQuery<ServiceMetricsResult>({
    queryKey: [...conversationQualityQueryKey, "service-metrics", input],
    queryFn: async () => {
      const result = await readQuality("serviceMetrics", input)
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    enabled,
    staleTime: 60_000,
  })
}

export function useConversationOutcomesQuery(input: QualityPeriodInput, enabled = true) {
  return useQuery<ConversationOutcomesResult>({
    queryKey: [...conversationQualityQueryKey, "outcomes", input],
    queryFn: async () => {
      const result = await readQuality("outcomes", input)
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    enabled,
    staleTime: 60_000,
  })
}

export function useUnresolvedTopicsQuery(input: QualityPeriodInput, enabled = true) {
  return useQuery<UnresolvedTopicsResult>({
    queryKey: [...conversationQualityQueryKey, "unresolved-topics", input],
    queryFn: async () => {
      const result = await readQuality("unresolvedTopics", input)
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    enabled,
    staleTime: 60_000,
  })
}

/** Under the quality key, so turning the feature on or off refreshes the card that says so. */
export const qualityDigestSettingsQueryKey = [...conversationQualityQueryKey, "digest-settings"] as const

export function useQualityDigestSettingsQuery() {
  return useQuery<QualityDigestSettingsView>({
    queryKey: qualityDigestSettingsQueryKey,
    queryFn: async () => {
      const result = await getQualityDigestSettingsAction({})
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function useQualitySuggestionsQuery(input: QualitySuggestionsInput, enabled = true) {
  return useQuery<QualitySuggestionsResult>({
    queryKey: [...qualitySuggestionsQueryKey, input],
    queryFn: async () => {
      const result = await readQuality("suggestions", input)
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    enabled,
    staleTime: 60_000,
  })
}

export function findImprovementsStatusQueryKey(input: QualitySuggestionsInput) {
  return [...findImprovementsQueryKey, input] as const
}

export function useFindImprovementsStatusQuery(input: QualitySuggestionsInput, enabled = true) {
  return useQuery<FindNowReport>({
    queryKey: findImprovementsStatusQueryKey(input),
    queryFn: async () => {
      const result = await readQuality("findNowStatus", input)
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    enabled,
    refetchInterval: (query) => refetchFindNow(query.state.data),
  })
}

/** While a run is under way, so its end is seen; and once a wait ends, so the button comes back by itself. */
function refetchFindNow(report: FindNowReport | undefined): number | false {
  const current = report?.current
  switch (current?.state) {
    case "running":
      return FIND_NOW_POLL_MS
    case "available_later":
      // Never faster than a run's poll: a browser clock ahead of the server passes the time early.
      return Math.max(Date.parse(current.availableAt) - Date.now() + 1_000, FIND_NOW_POLL_MS)
    case "available":
    case "nothing_new":
    case "not_drafted":
    case undefined:
      return false
    default: {
      const exhaustive: never = current
      return exhaustive
    }
  }
}

/**
 * Once a run seen running ends, the list refreshes, and a toast says what the press found. A press is told from a
 * weekly run holding the lock by the newest press's end changing, so no clock is compared.
 */
export function useFindImprovementsCompletion(report: FindNowReport | undefined) {
  const { findNowOutcome } = useQualityChanges()

  const queryClient = useQueryClient()
  // The newest press's end as it stood when a run was first seen; `undefined` while none is.
  const endBefore = useRef<string | null | undefined>(undefined)
  const running = report?.current.state === "running"
  const lastRun = report?.lastRun ?? null

  useEffect(() => {
    if (running) {
      if (endBefore.current === undefined) endBefore.current = lastRun?.finishedAt ?? null
      return
    }
    if (endBefore.current === undefined) return

    const before = endBefore.current
    endBefore.current = undefined
    void queryClient.invalidateQueries({ queryKey: qualitySuggestionsQueryKey })
    if (lastRun && lastRun.finishedAt !== before) {
      const { tone, message } = findNowOutcome(lastRun)
      toast[tone](message)
    }
  }, [running, lastRun, queryClient])
}

/**
 * The changes, newest first, a page at a time. Under the suggestions key, so a decision on the queue or an Undo
 * refreshes it too.
 */
export function useQualityChangesQuery(input: QualitySuggestionsInput, enabled = true) {
  const { CHANGES_FIRST_PAGE, CHANGES_PAGE } = useQualityChanges()

  return useInfiniteQuery({
    queryKey: [...qualitySuggestionsQueryKey, "changes", input],
    initialPageParam: null as ChangesCursor | null,
    queryFn: async ({ pageParam }) => {
      const result = await readQuality("changes", {
        ...input,
        cursor: pageParam,
        limit: pageParam === null ? CHANGES_FIRST_PAGE : CHANGES_PAGE,
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled,
    staleTime: 60_000,
  })
}

/** Overview's card. Under the suggestions key, so a decision, an Undo or a mode change refreshes it. */
export function useSelfLearningSummaryQuery(input: QualitySuggestionsInput, enabled = true) {
  return useQuery<SelfLearningSummaryResult>({
    queryKey: [...qualitySuggestionsQueryKey, "self-learning-summary", input],
    queryFn: async () => {
      const result = await readQuality("selfLearningSummary", input)
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    enabled,
    staleTime: 60_000,
  })
}

/** Read afresh each time a review opens: the diff is against the instructions as they are now. */
export function useSuggestionInstructionsQuery(suggestionId: string) {
  return useQuery<string>({
    queryKey: [...qualitySuggestionsQueryKey, "instructions", suggestionId],
    queryFn: async () => {
      const result = await getSuggestionInstructionsAction({ suggestionId })
      if ("status" in result) throw new Error(result.errorMessage)
      return result.instructions
    },
    staleTime: 0,
    gcTime: 0,
  })
}

export function useQualityAgentsQuery() {
  return useQuery<QualityAgentOption[]>({
    queryKey: [...conversationQualityQueryKey, "agents"],
    queryFn: async () => {
      const result = await readQuality("agents", {})
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    staleTime: 5 * 60_000,
  })
}

/** Under the suggestions key, so an Undo or a decision refreshes it; shared by every History row of the agent. */
export function useAgentQualityChangesQuery(agentId: string) {
  return useQuery<QualityChangeView[]>({
    queryKey: [...qualitySuggestionsQueryKey, "agent-changes", agentId],
    queryFn: async () => {
      const result = await listAgentQualityChangesAction({ agentId })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    staleTime: 60_000,
  })
}
